import { useState, type ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

import { mapControlProps } from '../../lib/map-control-props';

interface MapControlPopoverProps {
  icon: LucideIcon;
  /**
   * The map surface. The panel is portalled into it rather than into `document.body`
   * so it survives fullscreen, where the browser paints nothing outside the element
   * that was expanded.
   */
  container: HTMLElement | null;
  /**
   * Accessible name for the icon-only trigger. Not shown anywhere: the panel's own
   * contents are named, so a heading would only repeat them.
   */
  label: string;
  children: ReactNode;
}

/**
 * A map-corner control that keeps its panel folded away until asked for, the way
 * legacy RITA does with its layer and grouping controls — an always-open panel eats
 * the map it is meant to describe.
 */
export function MapControlPopover({ icon: Icon, label, container, children }: MapControlPopoverProps) {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button {...mapControlProps(open)} aria-label={label}>
          <Icon className="h-4 w-4" />
        </Button>
      </PopoverTrigger>

      {/* Inside the map's own stack now, so it has to outrank Leaflet's corners (1000). */}
      <PopoverContent container={container} align="end" className="z-[1001] w-60">
        {children}
      </PopoverContent>
    </Popover>
  );
}
